"use client";

import "client-only";
import posthog from "posthog-js";
import {
    safeAuthRedirectPath,
    type AuthTransitionSuccess,
} from "@/lib/auth/transition";
import { prepareAuthDocumentTransition } from "@/lib/auth/document-transition";

export interface AuthTransitionClientRuntime {
    prepareDocumentTransition?: () => void;
    resetAnalyticsIdentity: () => void;
    replaceLocation: (redirectTo: string) => void;
}

const browserRuntime: AuthTransitionClientRuntime = {
    prepareDocumentTransition: prepareAuthDocumentTransition,
    resetAnalyticsIdentity: () => {
        if (posthog.__loaded) posthog.reset();
    },
    replaceLocation: (redirectTo) => window.location.replace(redirectTo),
};

const AUTH_TRANSITION_FALLBACK_PATH = "/";

/**
 * Finish a server-side auth mutation by remounting the whole client tree.
 * Analytics cleanup is best-effort and must never strand the user on a page
 * whose persistent providers still represent the previous auth session.
 */
export function completeAuthTransition(
    transition: AuthTransitionSuccess,
    runtime: AuthTransitionClientRuntime = browserRuntime,
): void {
    const redirectTo =
        safeAuthRedirectPath(transition.redirectTo) ??
        AUTH_TRANSITION_FALLBACK_PATH;

    try {
        runtime.prepareDocumentTransition?.();
    } catch {
        // A defensive curtain failure must not prevent the session reload.
    }

    if (transition.resetAnalyticsIdentity) {
        try {
            runtime.resetAnalyticsIdentity();
        } catch {
            // A best-effort analytics reset must not block auth synchronization.
        }
    }

    runtime.replaceLocation(redirectTo);
}
